/**
 * i18next initialization for the web app.
 *
 * Mirrors mobile/src/i18n/init.ts:
 *   1. Sync-read localStorage for the user's last-known language + cached bundle.
 *   2. Init i18next synchronously with bundled `en` as fallback AND any
 *      cached bundle for the user's lang. No FOUT.
 *   3. Background-fetch /api/i18n/v1/bundle?lang=<lang>; on success,
 *      addResourceBundle + persist to localStorage.
 *
 * Profile-driven language switch is wired via web/src/i18n/use-i18n-sync.ts
 * — that hook reads `useProfileQuery().data.primaryLanguage` and calls
 * `setLanguage()` whenever it changes.
 *
 * XSS note: `interpolation.escapeValue: false` is correct for React (JSX
 * auto-escapes string children). Translation values must NEVER be passed
 * through `dangerouslySetInnerHTML`. Use react-i18next's `<Trans>` for
 * any rich-text markup.
 */
import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

import sharedI18nPkg from '@newswave/i18n/package.json';

import { englishCatalog } from './locales';

export const SUPPORTED_LANGS = ['en', 'sv', 'ar', 'fi', 'bs', 'hi', 'uk', 'th'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

/**
 * Schema version derives from `shared/i18n/package.json#version`'s major
 * component — single source of truth shared with Terraform's
 * `locals.i18n_schema_version`. Bumping the major in shared/i18n auto-
 * propagates to mobile, web, the api env var, and the GCS prefix.
 */
function resolveSchemaVersion(): number {
  const raw = sharedI18nPkg.version?.split('.')[0] ?? '1';
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

export const SCHEMA_VERSION = resolveSchemaVersion();

const LANG_KEY = 'newswave.i18n.lang';
const BUNDLE_KEY_PREFIX = 'newswave.i18n.v';
const BUNDLE_KEY = (lang: string): string => `${BUNDLE_KEY_PREFIX}${SCHEMA_VERSION}.${lang}`;
const BUNDLE_KEY_REGEX = /^newswave\.i18n\.v(\d+)\..+$/;

const FETCH_TIMEOUT_MS = 10_000;

/**
 * `VITE_API_BASE_URL` already includes the `/api` suffix (matches
 * `web/src/config/api-client.ts` and `web/.env.example`). Append only
 * the endpoint path here — doubling `/api/` would 404 silently and
 * users would never see translated copy.
 */
const apiBase: string = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, '') ?? '';

export function isSupportedLang(value: string | undefined | null): value is SupportedLang {
  if (!value) return false;
  return (SUPPORTED_LANGS as readonly string[]).includes(value);
}

export function normalizeLang(raw: string | undefined | null): SupportedLang | null {
  if (!raw) return null;
  const primary = raw.split('-')[0]?.toLowerCase() ?? '';
  return isSupportedLang(primary) ? primary : null;
}

// Memoize the resolved Storage so saveCachedBundle / loadCachedBundle
// / setLanguage don't each re-run the try/catch on every cold start.
let cachedStorage: Storage | null | undefined;
function safeStorage(): Storage | null {
  if (cachedStorage !== undefined) return cachedStorage;
  try {
    cachedStorage = typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    cachedStorage = null;
  }
  return cachedStorage;
}

function detectDeviceLocale(): SupportedLang | null {
  if (typeof navigator === 'undefined') return null;
  const candidates = navigator.languages ?? [navigator.language];
  for (const cand of candidates) {
    const norm = normalizeLang(cand);
    if (norm) return norm;
  }
  return null;
}

function resolveInitialLang(): SupportedLang {
  const ls = safeStorage();
  if (ls) {
    const stored = ls.getItem(LANG_KEY);
    const norm = normalizeLang(stored);
    if (norm) return norm;
  }
  return detectDeviceLocale() ?? 'en';
}

function loadCachedBundle(lang: SupportedLang): Record<string, unknown> | null {
  const ls = safeStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(BUNDLE_KEY(lang));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

function saveCachedBundle(lang: SupportedLang, data: Record<string, unknown>): void {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.setItem(BUNDLE_KEY(lang), JSON.stringify(data));
  } catch {
    // QuotaExceededError or private-mode lockdown — accept the miss.
  }
}

/**
 * Garbage-collect cache keys from older schema versions. Same idea as
 * mobile's `purgeStaleCacheKeys` — once on init.
 */
function purgeStaleCacheKeys(): void {
  const ls = safeStorage();
  if (!ls) return;
  try {
    const stale: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (!k) continue;
      const m = BUNDLE_KEY_REGEX.exec(k);
      if (m && Number.parseInt(m[1] ?? '', 10) < SCHEMA_VERSION) {
        stale.push(k);
      }
    }
    for (const k of stale) ls.removeItem(k);
  } catch {
    // best-effort
  }
}

function bundleUrl(lang: SupportedLang): string | null {
  if (!apiBase) return null;
  // apiBase already ends in `/api`; only the endpoint path is appended.
  return `${apiBase}/i18n/v1/bundle?lang=${encodeURIComponent(lang)}&v=${SCHEMA_VERSION}`;
}

export interface InitOptions {
  /** Skip the background remote fetch — used by tests. */
  readonly skipRemote?: boolean;
}

let initialized = false;

export function initI18n(options: InitOptions = {}): I18nInstance {
  if (initialized) return i18next;

  purgeStaleCacheKeys();

  const lang = resolveInitialLang();
  const cached = lang !== 'en' ? loadCachedBundle(lang) : null;

  const resources: Record<string, { translation: Record<string, unknown> }> = {
    en: { translation: englishCatalog as Record<string, unknown> },
  };
  if (cached) {
    resources[lang] = { translation: cached };
  }

  void i18next.use(initReactI18next).init({
    resources,
    lng: lang,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
    react: {
      useSuspense: false,
    },
  });

  initialized = true;

  // Skip remote refresh when the active locale is English: the bundled
  // catalog IS the source of truth for `en`. A stale CDN copy could
  // overwrite freshly bundled strings (e.g., app deployed before the
  // i18n-publish.yml workflow uploads the matching bundle). Non-`en`
  // locales DO want the refresh — translators add real translations
  // server-side while the bundled fallback stays English.
  if (!options.skipRemote && lang !== 'en') {
    void refreshLanguageInBackground(lang);
  }

  return i18next;
}

export async function refreshLanguageInBackground(lang: SupportedLang): Promise<void> {
  const url = bundleUrl(lang);
  if (!url) return;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, { method: 'GET', signal: controller.signal });
    if (!res.ok) return;
    const data: unknown = await res.json();
    if (typeof data !== 'object' || data === null || Array.isArray(data)) return;
    const bundle = data as Record<string, unknown>;
    saveCachedBundle(lang, bundle);
    if (initialized) {
      i18next.addResourceBundle(lang, 'translation', bundle, true, true);
    }
  } catch {
    // bundled fallback is still good
  } finally {
    clearTimeout(timer);
  }
}

export function setLanguage(rawLang: string): void {
  const lang = normalizeLang(rawLang);
  if (!lang) return;
  const ls = safeStorage();
  if (ls) {
    try {
      ls.setItem(LANG_KEY, lang);
    } catch {
      // ignore
    }
  }
  const sameLang = initialized && i18next.language === lang;
  if (lang !== 'en') {
    const cached = loadCachedBundle(lang);
    if (cached && initialized) {
      i18next.addResourceBundle(lang, 'translation', cached, true, true);
    }
    if (sameLang && cached) return;
  } else if (sameLang) {
    return;
  }
  if (initialized) {
    void i18next.changeLanguage(lang);
  }
  // English is bundled-source-of-truth; never overwrite from CDN.
  if (lang !== 'en') {
    void refreshLanguageInBackground(lang);
  }
}

/**
 * Vite HMR safety: re-importing init.ts during dev iteration leaves the
 * module-scoped `initialized` flag stuck at `true`, so the next call
 * returns the previous (now-stale) i18next instance. Resetting on
 * `dispose` keeps dev iteration sane. No-op in production.
 */
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    initialized = false;
    cachedStorage = undefined;
  });
}

/**
 * The raw i18next instance. `useTranslation()` from `react-i18next` is
 * the canonical entry point inside React components — direct
 * `i18next.t(...)` calls bypass the provider and won't trigger
 * re-renders on `languageChanged`.
 */
export { i18next };
